import { getDefaultDeviceType } from '@shared/constants/organization/ProjectDefaults';
import DeviceTypeModel, { DeviceTypeFieldName } from '@shared/models/devices/DeviceTypeModel';
import UpdateDeviceTypeRequest from '@shared/models/devices/UpdateDeviceTypeRequest';
import OrganizationDeviceTypeModel from '@shared/models/organizations/OrganizationDeviceTypeModel';
import IResult from '@shared/models/result/IResult';
import UnexpectedResult from '@shared/models/result/UnexpectedResult';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import AlertsContainer from './state/AlertsContainer';
import OrganizationContainer from './state/OrganizationContainer';
import useResolveCurrentOrg from './useResolveCurrentOrg';
import useStateRef from './useStateRef';

export function useCurrentOrgDeviceTypeEditor(initialModel: OrganizationDeviceTypeModel | null) {
    const { t } = useTranslation();
    const { organizationId } = useResolveCurrentOrg();
    const { deviceId } = useParams();
    const { updateOrgDeviceType, createOrgDeviceType, setOrgDeviceTypes } = OrganizationContainer.useContainer();
    const { showSuccess, showError } = AlertsContainer.useContainer();
    const [editDeviceType, setEditDeviceType, editDeviceTypeRef] = useStateRef<OrganizationDeviceTypeModel | null>(
        initialModel
    );

    useEffect(() => {
        if (initialModel) {
            setEditDeviceType(initialModel);
        } else {
            setEditDeviceType(getDefaultDeviceType(organizationId));
        }
    }, [initialModel]);

    const handleFieldChange = <T extends DeviceTypeFieldName>(fieldName: T, value: DeviceTypeModel[T]) => {
        setEditDeviceType((prevModel) =>
            prevModel
                ? {
                      ...prevModel,
                      [fieldName]: value,
                  }
                : null
        );
    };

    const handleInputChange = <T extends DeviceTypeFieldName>(fieldName: T, value: DeviceTypeModel[T]) => {
        handleFieldChange(fieldName, value);
    };

    const createUpdateRequest = () => {
        if (editDeviceTypeRef.current) {
            const updatedRequest: UpdateDeviceTypeRequest = editDeviceTypeRef.current as UpdateDeviceTypeRequest;
            return updatedRequest;
        }
    };

    const handleUpdate = async () => {
        try {
            const request = createUpdateRequest();
            if (!request || !organizationId || !deviceId) throw new Error('Missing data');
            const result = await updateOrgDeviceType(organizationId, deviceId, request);
            if (result.resultType === 'Ok' && result.data) {
                setEditDeviceType(result.data);
            }
            return result;
        } catch (error) {
            return new UnexpectedResult<DeviceTypeModel>();
        }
    };

    const handleCreate = async () => {
        try {
            const request = createUpdateRequest();
            if (!request || !organizationId) throw new Error('Missing data');
            const result = await createOrgDeviceType(organizationId, request);
            if (result.resultType === 'Ok' && result.data) {
                setEditDeviceType(result.data);
            }
            return result;
        } catch (error) {
            return new UnexpectedResult<DeviceTypeModel>();
        }
    };

    const handleAlerts = (result: IResult<DeviceTypeModel>) => {
        if (result.resultType === 'Ok') {
            showSuccess(t('common:saved'));
        } else {
            showError(t('common:saveError'));
        }
    };

    const handleSave = async () => {
        return editDeviceTypeRef.current?.id ? await handleUpdate() : await handleCreate();
    };

    const handleSaveWithAlerts = async () => {
        const result = await handleSave();
        handleAlerts(result);
        return result;
    };

    const handleCreateAndClearEditor = async () => {
        const result = await handleCreate();
        handleAlerts(result);
        if (result.resultType === 'Ok') {
            setEditDeviceType(getDefaultDeviceType(organizationId));
        }
        return result;
    };

    return {
        editDeviceType,
        setEditDeviceType,
        handleFieldChange,
        handleInputChange,
        handleSave,
        handleSaveWithAlerts,
        handleCreateAndClearEditor,
    };
}
